import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { RichTextDirective } from '@examdojo/core/widget';

import { compileExpression } from '../function-test/expression.compile';
import {
  deriveTransformInverse,
  type FunctionMachineBlock,
  type FunctionMachineConfig,
  type FunctionMachineFunctionBlock,
  type FunctionMachineTransformBlock,
  type FunctionMachineValueBlock,
  type RunningState,
} from './function-machine.model';

/** A visible block with position metadata */
interface VisibleBlock {
  block: FunctionMachineBlock;
  isFirst: boolean;
  isLast: boolean;
}

@Component({
  selector: 'dojo-function-machine',
  standalone: true,
  imports: [RichTextDirective],
  templateUrl: './function-machine.component.html',
  styleUrl: './function-machine.component.scss',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FunctionMachineComponent {
  constructor() {
    // Initialize input values from config (set inverse state first so effectiveBlocks resolves correctly)
    effect(
      () => {
        this.config(); // subscribe to config changes
        this.isInverseActive.set(this.config().params.inverse ?? false);

        const values = new Map<string, number>();
        for (const block of this.effectiveBlocks()) {
          if (block.type === 'value' && block.value !== undefined) {
            values.set(block.id, block.value);
          }
        }
        this.inputValues.set(values);
        this.computedValues.set(new Map());
        this.errors.set(new Map());
        this.runningState.set('idle');
        this.animatedElements.set(new Set());
      },
      { allowSignalWrites: true },
    );

    // Auto-run on input change
    effect(
      () => {
        const autoRun = this.config().behavior?.autoRun ?? false;
        // Read inputValues to subscribe to changes
        this.inputValues();
        if (autoRun && this.runningState() !== 'running') {
          this.runEvaluation(this.effectiveBlocks().length - 1);
        }
      },
      { allowSignalWrites: true },
    );
  }

  readonly config = input.required<FunctionMachineConfig>();

  readonly valueChange = output<{ blockId: string; value: number }>();
  readonly evaluationComplete = output<{ results: Record<string, number> }>();

  // ---------------------------------------------------------------------------
  // Internal state
  // ---------------------------------------------------------------------------

  readonly inputValues = signal<Map<string, number>>(new Map());
  readonly computedValues = signal<Map<string, number | null>>(new Map());
  readonly errors = signal<Map<string, string>>(new Map());
  readonly runningState = signal<RunningState>('idle');
  readonly animatedElements = signal<Set<string>>(new Set());
  readonly isInverseActive = signal(false);
  /** True while the animation cascade is active (highlights arrows, blocks). Reverts after delay. */
  readonly isHighlighted = signal(false);
  private readonly runningBlockIds = new Set<string>();

  // ---------------------------------------------------------------------------
  // Computed config helpers
  // ---------------------------------------------------------------------------

  readonly layout = computed(() => this.config().display?.layout ?? 'horizontal');
  readonly precision = computed(() => this.config().display?.precision ?? 4);

  /** Whether inverse mode is available — requires explicit inverse config on params or blocks */
  readonly hasInverse = computed(() => {
    if (this.config().params.inverse !== undefined) {
      return true;
    }
    return this.config().params.blocks.some((b) => {
      if (b.type === 'value') {
        return b.inverseLabel !== undefined || b.inverseEditable !== undefined;
      }
      if (b.type === 'function') {
        return b.inverseExpression !== undefined;
      }
      if (b.type === 'transform') {
        return b.inverseExpression !== undefined || b.inverseOperation !== undefined;
      }
      return false;
    });
  });

  /** Forward theme color — first explicit color from blocks, defaults to 'sky' */
  readonly forwardColorName = computed(() => {
    for (const b of this.config().params.blocks) {
      if (b.type === 'function') {
        return b.color;
      }
      if (b.type === 'transform' && b.color) {
        return b.color;
      }
    }
    return 'sky';
  });

  /** Inverse theme color — first explicit inverseColor from blocks, defaults to 'pink' */
  readonly inverseColorName = computed(() => {
    for (const b of this.config().params.blocks) {
      if (b.type === 'function' && b.inverseColor) {
        return b.inverseColor;
      }
      if (b.type === 'transform' && b.inverseColor) {
        return b.inverseColor;
      }
    }
    return 'pink';
  });

  /** Current theme color based on active direction */
  readonly themeColorName = computed(() =>
    this.isInverseActive() ? this.inverseColorName() : this.forwardColorName(),
  );

  /** Blocks with inverse overrides applied when inverse mode is active.
   *  Array order stays the same — evaluation direction reverses instead. */
  readonly effectiveBlocks = computed<FunctionMachineBlock[]>(() => {
    const blocks = this.config().params.blocks;
    if (!this.isInverseActive()) {
      return blocks;
    }
    return blocks.map((b) => {
      switch (b.type) {
        case 'value':
          return {
            ...b,
            value: b.inverseValue ?? b.value,
            label: b.inverseLabel ?? b.label,
            editable: b.inverseEditable ?? b.editable,
          } satisfies FunctionMachineValueBlock;
        case 'function':
          return {
            ...b,
            expression: b.inverseExpression ?? b.expression,
            name: b.inverseName ?? b.name,
            color: b.inverseColor ?? b.color,
          } satisfies FunctionMachineFunctionBlock;
        case 'transform': {
          const auto = deriveTransformInverse(b.expression, b.variable);
          return {
            ...b,
            expression: b.inverseExpression ?? auto?.expression ?? b.expression,
            operation: b.inverseOperation ?? auto?.operation ?? b.operation,
            color: b.inverseColor ?? b.color,
          } satisfies FunctionMachineTransformBlock;
        }
        default:
          return b;
      }
    });
  });

  /** True when inverse mode is active (arrows flip, expressions swap). */
  readonly isInverse = computed(() => this.isInverseActive());

  readonly showGlobalRun = computed(() => {
    const cfg = this.config().display;
    return cfg?.showRunButton !== false && (cfg?.runButtonMode ?? 'global') === 'global';
  });

  readonly showPerBlockRun = computed(() => {
    const cfg = this.config().display;
    return cfg?.showRunButton !== false && cfg?.runButtonMode === 'per-block';
  });

  /** Blocks visible in the chain, filtering out intermediates in compact mode */
  readonly visibleBlocks = computed<VisibleBlock[]>(() => {
    const allBlocks = this.effectiveBlocks();
    const compact = this.config().display?.compact ?? false;

    let blocks: FunctionMachineBlock[];
    if (compact) {
      blocks = allBlocks.filter((b, i) => {
        if (b.type !== 'value') {
          return true;
        }
        // Keep first and last value blocks
        const isFirst = allBlocks.findIndex((bb) => bb.type === 'value') === i;
        const isLast = allBlocks.findLastIndex((bb) => bb.type === 'value') === i;
        return isFirst || isLast;
      });
    } else {
      blocks = allBlocks;
    }

    return blocks.map((block, i) => ({
      block,
      isFirst: i === 0,
      isLast: i === blocks.length - 1,
    }));
  });

  /** Memoized compiled expressions (recomputes when effectiveBlocks change, e.g. inverse mode) */
  private readonly compiledExpressions = computed(() => {
    const blocks = this.effectiveBlocks();
    const compiled = new Map<string, (value: number) => number>();
    for (const block of blocks) {
      if (block.type === 'function' || block.type === 'transform') {
        const variable = block.variable ?? 'x';
        try {
          compiled.set(block.id, compileExpression(block.expression, variable));
        } catch {
          // Invalid expression — will error at evaluation time
        }
      }
    }
    return compiled;
  });

  // ---------------------------------------------------------------------------
  // Template helpers
  // ---------------------------------------------------------------------------

  getBlockLabel(block: FunctionMachineBlock, isFirst: boolean, isLast: boolean): string | null {
    if (block.type !== 'value') {
      return null;
    }
    if (block.label) {
      return block.label;
    }
    const inverse = this.isInverseActive();
    if (isFirst) {
      return inverse ? 'Output' : 'Input';
    }
    if (isLast) {
      return inverse ? 'Input' : 'Output';
    }
    return null;
  }

  shouldShowLabel(block: FunctionMachineBlock, isFirst: boolean, isLast: boolean): boolean {
    if (block.type !== 'value') {
      return false;
    }
    if (!this.getBlockLabel(block, isFirst, isLast)) {
      return false;
    }
    if (block.showLabel !== undefined) {
      return block.showLabel;
    }
    return isFirst || isLast;
  }

  getValueAriaLabel(block: FunctionMachineBlock, isFirst: boolean, isLast: boolean): string {
    const label = this.getBlockLabel(block, isFirst, isLast);
    return label ? `${label} value` : 'Intermediate value';
  }

  isEditable(block: FunctionMachineBlock): boolean {
    if (block.type !== 'value') {
      return false;
    }
    if (block.editable !== undefined) {
      return block.editable;
    }
    // Default: first value block is editable (forward), last value block (inverse)
    const allBlocks = this.effectiveBlocks();
    if (this.isInverseActive()) {
      const lastValue = [...allBlocks].reverse().find((b) => b.type === 'value');
      return lastValue?.id === block.id;
    }
    const firstValue = allBlocks.find((b) => b.type === 'value');
    return firstValue?.id === block.id;
  }

  getInputValue(block: FunctionMachineBlock): string {
    if (block.type !== 'value') {
      return '';
    }
    const inputVal = this.inputValues().get(block.id);
    if (inputVal !== undefined) {
      return this.formatNumber(inputVal);
    }
    if (block.value !== undefined) {
      return this.formatNumber(block.value);
    }
    return '';
  }

  getDisplayValue(block: FunctionMachineBlock): string {
    if (block.type !== 'value') {
      return '';
    }
    // Check computed values first
    const computedVal = this.computedValues().get(block.id);
    if (computedVal !== undefined && computedVal !== null) {
      return this.formatNumber(computedVal);
    }
    // Check input values
    const inputVal = this.inputValues().get(block.id);
    if (inputVal !== undefined) {
      return this.formatNumber(inputVal);
    }
    // Check default
    if (block.value !== undefined) {
      return this.formatNumber(block.value);
    }
    return '?';
  }

  hasComputedValue(blockId: string): boolean {
    return this.computedValues().has(blockId);
  }

  getExpressionDisplay(block: FunctionMachineFunctionBlock): string {
    const name = block.name;
    const variable = block.variable ?? 'x';
    const expr = block.expression;
    if (name) {
      return `$${name}(${variable}) = ${this.toKatex(expr)}$`;
    }
    return `$${this.toKatex(expr)}$`;
  }

  getOperationDisplay(block: FunctionMachineTransformBlock): string {
    const op = block.operation.replace(/×/g, '\\times ').replace(/÷/g, '\\div ').replace(/−/g, '-');
    return `$${op}$`;
  }

  isBlockRunning(blockId: string): boolean {
    return this.runningBlockIds.has(blockId);
  }

  getActiveColor(): string {
    return `var(--${this.themeColorName()}-500)`;
  }

  getTransformColorName(block: FunctionMachineTransformBlock): string {
    return block.color ?? this.themeColorName();
  }

  isElementAnimated(key: string): boolean {
    return this.animatedElements().has(key);
  }

  getPrimaryColor(): string {
    return this.forwardColorName();
  }

  getInverseColor(): string {
    return this.inverseColorName();
  }

  // ---------------------------------------------------------------------------
  // User interactions
  // ---------------------------------------------------------------------------

  onInputChange(blockId: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = parseFloat(target.value);
    if (!Number.isNaN(value)) {
      this.inputValues.update((m) => {
        const next = new Map(m);
        next.set(blockId, value);
        return next;
      });
      this.valueChange.emit({ blockId, value });
    }
  }

  onRunAll(): void {
    if (this.runningState() === 'running') {
      return;
    }
    const allBlocks = this.effectiveBlocks();
    void this.runEvaluation(allBlocks.length - 1);
  }

  onToggleInverse(): void {
    if (this.runningState() === 'running') {
      return;
    }

    // Capture computed values before switching mode (to carry over as input)
    const prevComputed = this.computedValues();
    this.isInverseActive.update((v) => !v);
    this.isHighlighted.set(false);

    // Carry over: if the block that is now editable had a computed value, use it as input
    for (const block of this.effectiveBlocks()) {
      if (block.type === 'value' && this.isEditable(block)) {
        const val = prevComputed.get(block.id);
        if (val !== null && val !== undefined) {
          this.inputValues.update((m) => new Map(m).set(block.id, val));
        }
      }
    }

    // Reset evaluation state (no auto-run — user clicks Run separately)
    this.computedValues.set(new Map());
    this.errors.set(new Map());
    this.animatedElements.set(new Set());
    this.runningBlockIds.clear();
  }

  onRunUpTo(visibleIndex: number): void {
    if (this.runningState() === 'running') {
      return;
    }
    // Map visible index to full block index
    const visibleBlock = this.visibleBlocks()[visibleIndex];
    if (!visibleBlock) {
      return;
    }
    const allBlocks = this.effectiveBlocks();
    const fullIndex = allBlocks.findIndex((b) => b.id === visibleBlock.block.id);
    if (fullIndex >= 0) {
      void this.runEvaluation(fullIndex);
    }
  }

  // ---------------------------------------------------------------------------
  // Evaluation engine
  // ---------------------------------------------------------------------------

  private async runEvaluation(upToIndex: number): Promise<void> {
    const allBlocks = this.effectiveBlocks();
    const isReverse = this.isInverseActive();
    const visible = this.visibleBlocks();
    const duration = this.config().behavior?.animationDuration ?? 600;
    const totalSteps = visible.length + Math.max(visible.length - 1, 0);
    const stepDelay = duration / Math.max(totalSteps, 1);

    this.runningState.set('running');
    this.animatedElements.set(new Set());
    this.isHighlighted.set(true);
    this.errors.update(() => new Map());
    this.computedValues.update(() => new Map());
    this.runningBlockIds.clear();

    // Phase 1: Evaluate all values upfront
    let currentValue = this.getFirstInputValue();
    let hitError = false;
    const evalResults = new Map<string, number>();

    const indices = isReverse
      ? Array.from({ length: allBlocks.length }, (_, i) => allBlocks.length - 1 - i)
      : Array.from({ length: Math.min(upToIndex + 1, allBlocks.length) }, (_, i) => i);

    for (const i of indices) {
      const block = allBlocks[i];
      if (block.type === 'function' || block.type === 'transform') {
        if (currentValue === null) {
          hitError = true;
          break;
        }
        const result = this.evaluateBlock(block.id, currentValue);
        if (result === null) {
          hitError = true;
          break;
        }
        currentValue = result;
      }
      if (block.type === 'value' && !this.isEditableById(block.id)) {
        if (currentValue !== null) {
          evalResults.set(block.id, currentValue);
        }
      }
    }

    // Phase 2: Animate sequentially (block → arrow → block → arrow → block)
    const addElement = (key: string) => {
      this.animatedElements.update((s) => {
        const next = new Set(s);
        next.add(key);
        return next;
      });
    };

    const animateStep = async (vi: number) => {
      const { block } = visible[vi];
      addElement(`block-${vi}`);

      if (block.type === 'function' || block.type === 'transform') {
        this.runningBlockIds.add(block.id);
      }
      if (block.type === 'value' && !this.isEditableById(block.id) && evalResults.has(block.id)) {
        this.computedValues.update((m) => new Map(m).set(block.id, evalResults.get(block.id)!));
      }

      await delay(stepDelay);
    };

    if (isReverse) {
      for (let vi = visible.length - 1; vi >= 0; vi--) {
        await animateStep(vi);
        if (vi > 0) {
          addElement(`arrow-${vi - 1}`);
          await delay(stepDelay);
        }
      }
    } else {
      for (let vi = 0; vi < visible.length; vi++) {
        const fullIdx = allBlocks.findIndex((b) => b.id === visible[vi].block.id);
        if (fullIdx > upToIndex) {
          break;
        }
        await animateStep(vi);
        if (vi < visible.length - 1) {
          addElement(`arrow-${vi}`);
          await delay(stepDelay);
        }
      }
    }

    this.runningBlockIds.clear();
    this.runningState.set(hitError ? 'idle' : 'complete');

    if (!hitError) {
      const results: Record<string, number> = {};
      for (const [id, val] of evalResults) {
        results[id] = val;
      }
      this.evaluationComplete.emit({ results });

      await delay(1500);
      this.isHighlighted.set(false);
    } else {
      this.isHighlighted.set(false);
    }
  }

  private evaluateBlock(blockId: string, inputValue: number): number | null {
    const fn = this.compiledExpressions().get(blockId);
    if (!fn) {
      this.errors.update((m) => new Map(m).set(blockId, 'Invalid expression'));
      return null;
    }

    try {
      const result = fn(inputValue);
      if (!Number.isFinite(result)) {
        this.errors.update((m) => new Map(m).set(blockId, 'Undefined'));
        return null;
      }
      return result;
    } catch {
      this.errors.update((m) => new Map(m).set(blockId, 'Error'));
      return null;
    }
  }

  private getFirstInputValue(): number | null {
    const blocks = this.effectiveBlocks();
    for (const block of blocks) {
      if (block.type === 'value' && this.isEditable(block)) {
        const inputVal = this.inputValues().get(block.id);
        if (inputVal !== undefined) {
          return inputVal;
        }
        if (block.value !== undefined) {
          return block.value;
        }
        return null;
      }
    }
    return null;
  }

  private isEditableById(blockId: string): boolean {
    const block = this.effectiveBlocks().find((b) => b.id === blockId);
    if (!block || block.type !== 'value') {
      return false;
    }
    return this.isEditable(block);
  }

  // ---------------------------------------------------------------------------
  // Formatting helpers
  // ---------------------------------------------------------------------------

  private formatNumber(value: number): string {
    const prec = this.precision();
    if (Number.isInteger(value)) {
      return String(value);
    }
    return parseFloat(value.toFixed(prec)).toString();
  }

  /** Convert a raw expression to simple KaTeX notation */
  private toKatex(expr: string): string {
    return (
      expr
        // Replace * with \cdot for multiplication
        .replace(/\s*\*\s*/g, ' \\cdot ')
        // Replace ** or ^ with superscript
        .replace(/\*\*(\d+)/g, '^{$1}')
    );
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
